import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, ArrowTopRightOnSquareIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import { enrichCampData } from '../lib/aiEnrichmentClient';
import { saveEnrichedCamp } from '../lib/enrichExistingCamp';
import { supabase, updateCampCategories } from '../lib/supabase';
import toast from 'react-hot-toast';

interface PasteEnrichmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  campId: string;
  campName?: string;
  currentWebsite?: string;
  onSuccess: () => void;
}

export default function PasteEnrichmentModal({
  isOpen,
  onClose,
  campId,
  campName,
  currentWebsite,
  onSuccess,
}: PasteEnrichmentModalProps) {
  const [pastedContent, setPastedContent] = useState('');
  const [isEnriching, setIsEnriching] = useState(false);
  const [progress, setProgress] = useState('');
  const [enrichmentResult, setEnrichmentResult] = useState<{
    success: boolean;
    description?: string | null;
    activities?: string[];
    amenities?: string[];
    minAge?: number | null;
    maxAge?: number | null;
    categories?: number;
    message?: string;
  } | null>(null);

  const handleEnrich = async () => {
    if (!pastedContent.trim()) {
      toast.error('Please paste the website content');
      return;
    }

    setIsEnriching(true);
    setProgress('🚀 Starting enrichment...');

    try {
      // Get the existing camp data
      setProgress('📋 Fetching camp data...');
      const { data: camp, error: fetchError } = await supabase
        .from('camps')
        .select('*')
        .eq('id', campId)
        .single();

      if (fetchError || !camp) {
        throw new Error('Camp not found');
      }

      // Prepare enrichment request with pasted content
      setProgress('🤖 Processing content with AI...');
      const enrichmentRequest = {
        campName: camp.name,
        website: currentWebsite || camp.website_url || undefined,
        description: camp.description || undefined,
        address: camp.address || undefined,
        city: camp.city || undefined,
        state: camp.state || undefined,
        zipCode: camp.zip_code || undefined,
        latitude: camp.latitude || undefined,
        longitude: camp.longitude || undefined,
        email: camp.email || undefined,
        phone: camp.phone || undefined,
        slug: camp.slug || undefined,
        suggested_images: camp.suggested_images || undefined,
        contactInfo: camp.phone || undefined,
        // Pass pasted content as scraped content
        campPagesData: {
          mainPageContent: pastedContent,
          subPagesContent: [],
          currentYearInfo: [],
          nextYearInfo: [],
        },
      };

      const enrichmentResponse = await enrichCampData(enrichmentRequest, (msg) => {
        setProgress(msg);
      });

      console.log('📦 Enrichment response received:', {
        hasResponse: !!enrichmentResponse,
        hasDescription: !!enrichmentResponse?.description,
        descriptionLength: enrichmentResponse?.description?.length || 0,
        hasActivities: Array.isArray(enrichmentResponse?.activities),
        activitiesCount: Array.isArray(enrichmentResponse?.activities) ? enrichmentResponse.activities.length : 0,
        hasAmenities: Array.isArray(enrichmentResponse?.amenities),
        amenitiesCount: Array.isArray(enrichmentResponse?.amenities) ? enrichmentResponse.amenities.length : 0,
        responseKeys: enrichmentResponse ? Object.keys(enrichmentResponse) : [],
      });

      if (!enrichmentResponse) {
        throw new Error('Enrichment failed - no response received');
      }

      // Prepare update data
      setProgress('💾 Preparing to save...');
      const updateData = {
        description: enrichmentResponse.description || camp.description,
        short_description: enrichmentResponse.short_description || camp.short_description,
        min_age: enrichmentResponse.min_age || camp.min_age,
        max_age: enrichmentResponse.max_age || camp.max_age,
        start_hour_1: enrichmentResponse.start_hour_1 || camp.start_hour_1,
        finish_hour_1: enrichmentResponse.finish_hour_1 || camp.finish_hour_1,
        start_hour_2: enrichmentResponse.start_hour_2 || camp.start_hour_2,
        finish_hour_2: enrichmentResponse.finish_hour_2 || camp.finish_hour_2,
        extended_time_available: enrichmentResponse.extended_time_available ?? camp.extended_time_available,
        lunch_provided: enrichmentResponse.lunch_provided ?? camp.lunch_provided,
        lunch_fee: enrichmentResponse.lunch_fee || camp.lunch_fee,
        five_day_cost: enrichmentResponse.five_day_cost || camp.five_day_cost,
        dates_available: enrichmentResponse.dates_available || camp.dates_available,
        notes: enrichmentResponse.notes || camp.notes,
        activities: enrichmentResponse.activities || camp.activities,
        amenities: enrichmentResponse.amenities || camp.amenities,
        email: enrichmentResponse.email || camp.email,
        address: enrichmentResponse.address || camp.address,
        phone: enrichmentResponse.phone || camp.phone,
        latitude: enrichmentResponse.latitude || camp.latitude,
        longitude: enrichmentResponse.longitude || camp.longitude,
        blocking_reason: enrichmentResponse.blocking_reason || camp.blocking_reason,
        insufficient_content: enrichmentResponse.insufficient_content ?? camp.insufficient_content,
        social_media: enrichmentResponse.social_media || camp.social_media || null,
        suggested_categories: enrichmentResponse.suggested_categories || camp.suggested_categories || null,
        ai_enriched: {
          enrichments: [
            ...(camp.ai_enriched?.enrichments || []),
            {
              timestamp: new Date().toISOString(),
              version: 'v2',
              tokens_used: enrichmentResponse.enrichment_metadata?.tokens_used || 0,
              cost: enrichmentResponse.enrichment_metadata?.cost || 0,
              agent: enrichmentResponse.enrichment_metadata?.agent || 'gpt-4',
              model: enrichmentResponse.enrichment_metadata?.model || 'gpt-4',
              enrichment_mode: 'manual_paste'
            }
          ],
          total_enrichments: (camp.ai_enriched?.total_enrichments || 0) + 1,
          last_enriched: new Date().toISOString()
        }
      };

      // Save to database
      setProgress('💾 Saving to database...');
      console.log('💾 Saving updateData to database:', {
        campId,
        updateDataKeys: Object.keys(updateData),
        descriptionLength: updateData.description?.length || 0,
        activitiesCount: Array.isArray(updateData.activities) ? updateData.activities.length : 0,
        amenitiesCount: Array.isArray(updateData.amenities) ? updateData.amenities.length : 0,
      });
      
      const saveResult = await saveEnrichedCamp(campId, updateData);

      if (!saveResult.success) {
        console.error('❌ Database save failed:', saveResult);
        throw new Error(saveResult.message);
      }
      
      console.log('✅ Database save successful');

      // Save categories to camp_category_relations
      if (enrichmentResponse.suggested_categories) {
        setProgress('🏷️ Saving categories...');
        try {
          const categoryIds = await convertSuggestedCategoriesToIds(enrichmentResponse.suggested_categories);
          console.log('🏷️ Category IDs to save:', categoryIds);
          
          if (categoryIds.length > 0) {
            const categorySuccess = await updateCampCategories(campId, categoryIds);
            if (categorySuccess) {
              console.log('✅ Categories saved successfully');
              setProgress('✅ Categories saved successfully');
            } else {
              console.warn('⚠️ Failed to save categories');
            }
          } else {
            console.log('⚠️ No valid categories found to save');
          }
        } catch (categoryError) {
          console.error('❌ Error saving categories:', categoryError);
          // Don't fail the whole operation if categories fail
        }
      }

      // Save enrichment data file
      try {
        const { data: updatedCamp } = await supabase
          .from('camps')
          .select('id, name, slug')
          .eq('id', campId)
          .single();

        if (updatedCamp) {
          const enrichmentDataFile = {
            camp_id: updatedCamp.id,
            camp_name: updatedCamp.name,
            camp_slug: updatedCamp.slug,
            website_url: currentWebsite || camp.website_url || '',
            enrichment_timestamp: new Date().toISOString(),
            enrichment_mode: 'paste_enrichment',
            enrichment_response: enrichmentResponse,
            update_data: updateData,
            original_camp_data: camp,
            pasted_content: pastedContent.substring(0, 5000), // Store first 5000 chars for reference
          };

          const apiUrl = '/api/save-enrichment-data';
          await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ enrichmentData: enrichmentDataFile }),
          });
        }
      } catch (fileError) {
        console.error('Failed to save enrichment data file:', fileError);
        // Don't fail the whole operation
      }

      setProgress('✅ Enrichment completed!');
      
      // Store enrichment result for display
      const categoryCount = enrichmentResponse.suggested_categories 
        ? Object.values(enrichmentResponse.suggested_categories).reduce((total: number, arr: any) => {
            return total + (Array.isArray(arr) ? arr.length : 0);
          }, 0)
        : 0;
      
      setEnrichmentResult({
        success: true,
        description: enrichmentResponse.description,
        activities: enrichmentResponse.activities || [],
        amenities: enrichmentResponse.amenities || [],
        minAge: enrichmentResponse.min_age,
        maxAge: enrichmentResponse.max_age,
        categories: categoryCount,
        message: 'Camp enriched successfully!'
      });
      
      toast.success('Camp enriched successfully!');
      
      // Call onSuccess to refresh the camp data
      onSuccess();
    } catch (error) {
      console.error('❌ Error enriching camp:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Full error details:', {
        error,
        message: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      // Store error result for display
      setEnrichmentResult({
        success: false,
        message: errorMessage
      });
      
      toast.error(`Enrichment failed: ${errorMessage}`);
      setProgress(`❌ Error: ${errorMessage}`);
    } finally {
      setIsEnriching(false);
    }
  };

  const handleCloseAfterSuccess = () => {
    setEnrichmentResult(null);
    setPastedContent('');
    setProgress('');
    setIsEnriching(false);
    onClose();
  };

  const handleClose = () => {
    if (!isEnriching && !enrichmentResult?.success) {
      setEnrichmentResult(null);
      setPastedContent('');
      setProgress('');
      onClose();
    }
  };

  // Helper function to convert suggested_categories to category IDs
  const convertSuggestedCategoriesToIds = async (suggestedCategories: any): Promise<string[]> => {
    const categoryIds: string[] = [];
    
    try {
      // Handle different possible structures of suggested_categories
      const allCategoryNames: string[] = [];
      
      if (typeof suggestedCategories === 'object' && suggestedCategories !== null) {
        // If it's an object with category type keys (focus_area, format, etc.)
        Object.values(suggestedCategories).forEach((categoryArray: any) => {
          if (Array.isArray(categoryArray)) {
            categoryArray.forEach((categoryName: string) => {
              if (typeof categoryName === 'string' && categoryName.trim()) {
                allCategoryNames.push(categoryName.trim());
              }
            });
          }
        });
      }
      
      console.log('🏷️ Category names to look up:', allCategoryNames);
      
      if (allCategoryNames.length === 0) {
        return [];
      }
      
      // Fetch all categories from database
      const { data: allCategories, error: fetchError } = await supabase
        .from('camp_categories')
        .select('id, name');
      
      if (fetchError) {
        console.error('Error fetching categories:', fetchError);
        return [];
      }
      
      // Match category names (case-insensitive)
      allCategoryNames.forEach(categoryName => {
        const matchedCategory = allCategories?.find(
          cat => cat.name.toLowerCase() === categoryName.toLowerCase()
        );
        if (matchedCategory) {
          categoryIds.push(matchedCategory.id);
        } else {
          console.warn(`⚠️ Category not found in database: "${categoryName}"`);
        }
      });
      
      console.log('🏷️ Matched category IDs:', categoryIds);
      return categoryIds;
    } catch (error) {
      console.error('Error converting suggested categories:', error);
      return [];
    }
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setEnrichmentResult(null);
      setPastedContent('');
      setProgress('');
      setIsEnriching(false);
    }
  }, [isOpen]);

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                    <ClipboardIcon className="h-5 w-5 mr-2 text-green-500" />
                    Paste Enrichment
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    disabled={isEnriching}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-4">
                  {campName && (
                    <p className="text-sm text-gray-600 mb-2">
                      Enriching: <span className="font-medium">{campName}</span>
                    </p>
                  )}
                  
                  {currentWebsite && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Camp Website</p>
                          <p className="text-sm font-mono text-gray-900 break-all">{currentWebsite}</p>
                        </div>
                        <a
                          href={currentWebsite}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-4 flex-shrink-0 text-primary hover:text-primary/80"
                          title="Open in new tab"
                        >
                          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  )}

                  <label htmlFor="pasted-content" className="block text-sm font-medium text-gray-700 mb-2">
                    Paste Website Content
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Copy the main content from the camp's website and paste it here. Include information about activities, 
                    schedules, pricing, age ranges, and any other relevant details.
                  </p>
                  <textarea
                    id="pasted-content"
                    value={pastedContent}
                    onChange={(e) => setPastedContent(e.target.value)}
                    placeholder="Paste the website content here..."
                    disabled={isEnriching}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    {pastedContent.length} characters
                  </p>
                </div>

                {progress && !enrichmentResult && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">{progress}</p>
                  </div>
                )}

                {/* Success Result Display */}
                {enrichmentResult?.success && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-green-800 mb-2">
                          ✅ Enrichment Successful!
                        </h4>
                        <div className="text-sm text-green-700 space-y-1">
                          {enrichmentResult.description && (
                            <p><span className="font-medium">Description:</span> {enrichmentResult.description.length} characters extracted</p>
                          )}
                          {enrichmentResult.activities && enrichmentResult.activities.length > 0 && (
                            <p><span className="font-medium">Activities:</span> {enrichmentResult.activities.length} found</p>
                          )}
                          {enrichmentResult.amenities && enrichmentResult.amenities.length > 0 && (
                            <p><span className="font-medium">Amenities:</span> {enrichmentResult.amenities.length} found</p>
                          )}
                          {(enrichmentResult.minAge || enrichmentResult.maxAge) && (
                            <p><span className="font-medium">Age Range:</span> {enrichmentResult.minAge || '?'} - {enrichmentResult.maxAge || '?'} years</p>
                          )}
                          {enrichmentResult.categories && enrichmentResult.categories > 0 && (
                            <p><span className="font-medium">Categories:</span> {enrichmentResult.categories} assigned</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Result Display */}
                {enrichmentResult && !enrichmentResult.success && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h4 className="text-sm font-medium text-red-800 mb-1">
                          ❌ Enrichment Failed
                        </h4>
                        <p className="text-sm text-red-700">{enrichmentResult.message}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-4">
                  {enrichmentResult?.success ? (
                    <button
                      onClick={handleCloseAfterSuccess}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Done
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleClose}
                        disabled={isEnriching}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleEnrich}
                        disabled={isEnriching || !pastedContent.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {isEnriching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Enriching...
                          </>
                        ) : (
                          <>
                            <SparklesIcon className="h-4 w-4 mr-2" />
                            Enrich Camp
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
